import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Check, Target, PieChart, 
  Users, ListTodo, Layout, BarChart3, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';

/* ── Scroll reveal hook ── */
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const Reveal = ({ children, className = '', direction = 'up', delay = 0 }) => {
  const [ref, visible] = useScrollReveal();
  const origins = {
    up: 'translateY(60px)',
    down: 'translateY(-60px)',
    left: 'translateX(-80px)',
    right: 'translateX(80px)',
    scale: 'scale(0.92)',
  };
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) translateX(0) scale(1)' : origins[direction],
        transition: `opacity 0.8s cubic-bezier(.22,1,.36,1) ${delay}s, transform 0.8s cubic-bezier(.22,1,.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

/* ── Module showcase card mockups ── */
const StrategyDevMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-blue-500/30 bg-[#0a0a0a] p-6 aspect-[4/3] group transition-all duration-500 shadow-[0_0_40px_-10px_rgba(59,130,246,.2)]">
    <div className="flex items-center gap-2 mb-5">
      <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
    </div>
    <div className="flex gap-4">
      <div className="w-1/4 space-y-2">
        {['Company Brief','SWOT','Vision','Objectives'].map((s,i) => (
          <div key={i} className={`text-xs py-2 px-2.5 rounded-lg transition-all duration-300 ${i === 0 ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-white/5 text-gray-500'}`}>{s}</div>
        ))}
      </div>
      <div className="flex-1 space-y-3">
        <div className="h-3 bg-white/10 rounded-full w-3/4 animate-pulse" style={{animationDelay: '0s'}} />
        <div className="h-3 bg-white/10 rounded-full w-full animate-pulse" style={{animationDelay: '0.2s'}} />
        <div className="h-3 bg-white/10 rounded-full w-2/3 animate-pulse" style={{animationDelay: '0.4s'}} />
        <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3 h-3 text-blue-400" />
            <span className="text-xs text-blue-400 font-medium">AI Generated</span>
          </div>
          <div className="space-y-1.5">
            <div className="h-2 bg-blue-400/20 rounded-full w-full" />
            <div className="h-2 bg-blue-400/20 rounded-full w-4/5" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ExecutionMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-purple-500/30 bg-[#0a0a0a] p-4 md:p-6 aspect-[4/3] group transition-all duration-500 shadow-[0_0_40px_-10px_rgba(168,85,247,.2)]">
    <div className="flex items-center justify-between mb-3 md:mb-5">
      <div className="text-sm md:text-sm text-white font-semibold tracking-wide">Balanced Scorecard</div>
      <div className="text-xs md:text-xs text-purple-400 bg-purple-500/10 px-2.5 py-1 md:px-2.5 md:py-1 rounded-full font-medium">FY 2026</div>
    </div>
    <div className="grid grid-cols-2 gap-3 md:gap-4">
      {[
        { label: 'Financial', value: '87', color: '#22c55e', ring: 'border-green-500/30' },
        { label: 'Customer', value: '72', color: '#3b82f6', ring: 'border-blue-500/30' },
        { label: 'Internal Process', value: '91', color: '#a855f7', ring: 'border-purple-500/30' },
        { label: 'Org. Capacity', value: '65', color: '#f97316', ring: 'border-orange-500/30' },
      ].map((kpi, i) => (
        <div key={i} className={`bg-white/[0.03] rounded-xl p-3 md:p-4 border ${kpi.ring}`}>
          <div className="text-xs md:text-xs text-gray-500 mb-2 md:mb-3 font-medium">{kpi.label}</div>
          <div className="flex items-baseline gap-1 mb-2 md:mb-3">
            <span className="text-2xl md:text-3xl font-bold text-white leading-none">{kpi.value}</span>
            <span className="text-xs md:text-xs text-gray-500">/ 100</span>
          </div>
          <div className="w-full h-2 md:h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${kpi.value}%`, background: kpi.color }} />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-3 md:mt-4 flex items-center gap-2 text-xs md:text-xs text-gray-500">
      <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
      Overall: <span className="text-white font-semibold">78.8%</span> — On Target
    </div>
  </div>
);

const PortfolioMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-orange-500/30 bg-[#0a0a0a] p-6 aspect-[4/3] group transition-all duration-500 shadow-[0_0_40px_-10px_rgba(249,115,22,.2)]">
    <div className="flex items-center justify-between mb-5">
      <div className="text-sm text-white font-semibold tracking-wide">Portfolio Health</div>
      <div className="text-xs text-orange-400 bg-orange-500/10 px-2.5 py-1 rounded-full font-medium">16 Initiatives</div>
    </div>
    <div className="flex gap-6 mb-5">
      <div className="relative w-28 h-28 flex-shrink-0">
        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
          <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
          <circle cx="18" cy="18" r="14" fill="none" stroke="#22c55e" strokeWidth="3" strokeDasharray="66 34" strokeLinecap="round" />
          <circle cx="18" cy="18" r="14" fill="none" stroke="#eab308" strokeWidth="3" strokeDasharray="19 81" strokeDashoffset="-66" strokeLinecap="round" />
          <circle cx="18" cy="18" r="14" fill="none" stroke="#ef4444" strokeWidth="3" strokeDasharray="6 94" strokeDashoffset="-85" strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold text-white leading-none">75%</span>
          <span className="text-[9px] text-gray-500">Healthy</span>
        </div>
      </div>
      <div className="flex flex-col justify-center gap-3 flex-1">
        {[
          { l: 'On Track', v: '12', c: 'bg-green-500', tc: 'text-green-400' },
          { l: 'At Risk', v: '3', c: 'bg-yellow-500', tc: 'text-yellow-400' },
          { l: 'Delayed', v: '1', c: 'bg-red-500', tc: 'text-red-400' },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${s.c}`} />
            <span className="text-xs text-gray-400 flex-1">{s.l}</span>
            <span className={`text-sm font-bold ${s.tc}`}>{s.v}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">Budget Utilization</span>
        <span className="text-xs text-orange-400 font-semibold">$2.4M / $3.1M</span>
      </div>
      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-400" style={{ width: '77%' }} />
      </div>
    </div>
  </div>
);

const ProjectMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-emerald-500/30 bg-[#0a0a0a] p-6 aspect-[4/3] group transition-all duration-500 shadow-[0_0_40px_-10px_rgba(16,185,129,.2)]">
    <div className="flex items-center justify-between mb-5">
      <div className="text-sm text-white font-semibold tracking-wide">Active Projects</div>
      <div className="text-xs text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full font-medium">4 Active</div>
    </div>
    <div className="space-y-3">
      {[
        { title: 'Q1 Digital Transformation', owner: 'SK', status: 'On Track', color: 'bg-green-500', progress: 78, barColor: '#22c55e' },
        { title: 'Market Expansion APAC', owner: 'MR', status: 'At Risk', color: 'bg-yellow-500', progress: 45, barColor: '#eab308' },
        { title: 'Cost Optimization Program', owner: 'AL', status: 'On Track', color: 'bg-green-500', progress: 92, barColor: '#22c55e' },
        { title: 'Product Innovation Lab', owner: 'JD', status: 'Delayed', color: 'bg-red-500', progress: 23, barColor: '#ef4444' },
      ].map((p, i) => (
        <div key={i} className="bg-white/[0.03] rounded-xl p-3.5 border border-white/5">
          <div className="flex items-center gap-3 mb-2.5">
            <div className={`w-2 h-2 rounded-full ${p.color} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium truncate">{p.title}</div>
            </div>
            <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-[9px] text-gray-400 font-semibold flex-shrink-0">{p.owner}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${p.progress}%`, background: p.barColor }} />
            </div>
            <span className="text-xs font-mono text-gray-400 w-8 text-right">{p.progress}%</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MeetingMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-pink-500/30 bg-[#0a0a0a] p-6 aspect-[4/3] group transition-all duration-500 shadow-[0_0_40px_-10px_rgba(236,72,153,.2)]">
    <div className="bg-white/5 rounded-xl p-4 border border-white/5 mb-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-white">Board Strategy Review</span>
        <span className="text-xs text-pink-400 bg-pink-500/10 px-2 py-0.5 rounded-full">Live</span>
      </div>
      <div className="text-xs text-gray-500 mb-3">Agenda: Q1 Results, KPI Review, FY27 Planning</div>
      <div className="flex -space-x-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 border-2 border-[#0a0a0a]" />
        ))}
        <div className="w-6 h-6 rounded-full bg-white/10 border-2 border-[#0a0a0a] flex items-center justify-center text-[8px] text-gray-400">+3</div>
      </div>
    </div>
    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
      <div className="text-xs text-gray-500 mb-2">AI-Generated Minutes</div>
      <div className="space-y-1.5">
        <div className="h-2 bg-pink-400/10 rounded-full w-full" />
        <div className="h-2 bg-pink-400/10 rounded-full w-3/4" />
        <div className="h-2 bg-pink-400/10 rounded-full w-5/6" />
      </div>
    </div>
  </div>
);

const ActionItemsMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-yellow-500/30 bg-[#0a0a0a] p-6 aspect-[4/3] group transition-all duration-500 shadow-[0_0_40px_-10px_rgba(234,179,8,.2)]">
    <div className="flex items-center justify-between mb-5">
      <div className="text-sm text-white font-semibold tracking-wide">Board Action Items</div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-yellow-400 bg-yellow-500/10 px-2.5 py-1 rounded-full font-medium">4 Open</span>
        <span className="text-xs text-gray-500 bg-white/5 px-2.5 py-1 rounded-full font-medium">2 Done</span>
      </div>
    </div>
    <div className="space-y-2.5">
      {[
        { t: 'Approve FY27 strategic budget allocation', d: 'Mar 10', p: 'Board', owner: 'Chairman', done: true, pri: 'Critical' },
        { t: 'Review CEO succession planning report', d: 'Mar 15', p: 'Governance', owner: 'Lead Director', done: true, pri: 'High' },
        { t: 'Sign off on M&A due diligence findings', d: 'Mar 25', p: 'Board', owner: 'CFO', done: false, pri: 'Critical' },
        { t: 'Evaluate Q1 risk exposure dashboard', d: 'Apr 1', p: 'Audit Committee', owner: 'CRO', done: false, pri: 'High' },
        { t: 'Approve revised dividend policy', d: 'Apr 5', p: 'Board', owner: 'Treasurer', done: false, pri: 'Medium' },
        { t: 'Review ESG compliance framework', d: 'Apr 12', p: 'Governance', owner: 'CSO', done: false, pri: 'Medium' },
      ].map((item, i) => (
        <div key={i} className={`bg-white/[0.03] rounded-xl p-3 border border-white/5 flex items-center gap-3 transition-all ${item.done ? 'opacity-40' : ''}`}>
          <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${item.done ? 'border-yellow-500 bg-yellow-500/20' : 'border-white/15'}`}>
            {item.done && <Check className="w-3 h-3 text-yellow-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-medium ${item.done ? 'line-through text-gray-600' : 'text-white'}`}>{item.t}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-gray-600">{item.p}</span>
              <span className="text-[10px] text-gray-700">|</span>
              <span className="text-[10px] text-gray-600">{item.owner}</span>
              <span className="text-[10px] text-gray-700">|</span>
              <span className="text-[10px] text-gray-600">Due {item.d}</span>
            </div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
            item.pri === 'Critical' ? 'bg-red-500/10 text-red-400' :
            item.pri === 'High' ? 'bg-orange-500/10 text-orange-400' :
            'bg-blue-500/10 text-blue-400'
          }`}>{item.pri}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ── Module data ── */
const modules = [
  {
    title: 'Strategy Development',
    description: 'AI-powered strategy documentation that turns abstract goals into concrete, actionable plans. Generate entire strategy sections with one click.',
    features: ['12-section strategy editor with AI generation', 'Context-aware content for every entity', 'Export to PPTX & DOCX with one click', 'Version history and real-time collaboration'],
    icon: Target,
    color: 'blue',
    gradientFrom: 'from-blue-600',
    gradientTo: 'to-blue-400',
    glowColor: 'bg-blue-500',
    Mockup: StrategyDevMockup,
  },
  {
    title: 'Strategy Execution',
    description: 'Live Balanced Scorecards and KPIs that connect high-level strategy to day-to-day operations. Track performance across every perspective.',
    features: ['Balanced Scorecard with 4 perspectives', 'Real-time KPI dashboards and trend analysis', 'Cascading objectives from corporate to department', 'Automated status tracking and alerts'],
    icon: BarChart3,
    color: 'purple',
    gradientFrom: 'from-purple-600',
    gradientTo: 'to-purple-400',
    glowColor: 'bg-purple-500',
    Mockup: ExecutionMockup,
  },
  {
    title: 'Portfolio Management',
    description: "Bird's-eye view of all strategic initiatives. Monitor portfolio health, resource allocation, and strategic alignment across the entire organization.",
    features: ['Multi-project portfolio dashboard', 'Resource allocation & capacity planning', 'Strategic alignment tracking', 'Risk assessment and escalation management'],
    icon: PieChart,
    color: 'orange',
    gradientFrom: 'from-orange-600',
    gradientTo: 'to-orange-400',
    glowColor: 'bg-orange-500',
    Mockup: PortfolioMockup,
  },
  {
    title: 'Project Management',
    description: 'Seamlessly link strategic initiatives to executable projects. Track milestones, resources, and dependencies in one unified view.',
    features: ['Strategy-linked project creation', 'Gantt charts and milestone tracking', 'Resource allocation and team management', 'Cross-project dependency mapping'],
    icon: Layout,
    color: 'emerald',
    gradientFrom: 'from-emerald-600',
    gradientTo: 'to-emerald-400',
    glowColor: 'bg-emerald-500',
    Mockup: ProjectMockup,
  },
  {
    title: 'Meeting Management',
    description: 'Transform meetings into outcomes. Smart agendas, AI-generated minutes, and automatically assigned action items that keep teams aligned.',
    features: ['Structured agenda builder with time tracking', 'AI-powered meeting minutes generation', 'Auto-assigned action items with deadlines', 'Zoom & Teams integration'],
    icon: Users,
    color: 'pink',
    gradientFrom: 'from-pink-600',
    gradientTo: 'to-pink-400',
    glowColor: 'bg-pink-500',
    Mockup: MeetingMockup,
  },
  {
    title: 'Action Items Management',
    description: 'Centralized tracking for every commitment across the organization. Never lose sight of who owes what, by when. Cross-module accountability made simple.',
    features: ['Cross-module action tracking', 'Automatic deadline reminders', 'Priority-based task management', 'Team accountability dashboards'],
    icon: ListTodo,
    color: 'yellow',
    gradientFrom: 'from-yellow-600',
    gradientTo: 'to-yellow-400',
    glowColor: 'bg-yellow-500',
    Mockup: ActionItemsMockup,
  },
];

/* ── Module showcase section — alternating with mockup illustrations ── */
const LOGO_NAVY = '#1c3d59';

/* Compass SVG component for reuse - shows as a PORTION/FRAGMENT */
/* mobilePosition allows different positioning on mobile vs desktop */
const CompassFragment = ({ position, mobilePosition, size = 400, mobileSize, opacity = 0.18 }) => {
  // Position styles - compass positioned so only a portion shows
  const positionStyles = {
    'top-right': { top: '-35%', right: '-20%' },
    'top-left': { top: '-35%', left: '-20%' },
    'bottom-right': { bottom: '-35%', right: '-20%' },
    'bottom-left': { bottom: '-35%', left: '-20%' },
    'middle-right': { top: '15%', right: '-30%' },
    'middle-left': { top: '15%', left: '-30%' },
    // More prominent positions for mobile - closer to center
    'mobile-top-right': { top: '-25%', right: '-15%' },
    'mobile-top-left': { top: '-25%', left: '-15%' },
    'mobile-bottom-right': { bottom: '-20%', right: '-10%' },
    'mobile-bottom-left': { bottom: '-20%', left: '-10%' },
    'mobile-middle-right': { top: '10%', right: '-15%' },
    'mobile-middle-left': { top: '10%', left: '-15%' },
  };

  const desktopPos = positionStyles[position];
  const mobilePos = positionStyles[mobilePosition || position];
  const actualMobileSize = mobileSize || size;

  return (
    <>
      {/* Desktop version */}
      <svg 
        className="compass-slow-rotate absolute pointer-events-none hidden md:block"
        style={{ ...desktopPos, width: size, height: size, opacity }}
        viewBox="0 0 400 400"
      >
        <circle cx="200" cy="200" r="195" fill="none" stroke="rgba(186,230,253,0.7)" strokeWidth="1.2" />
        <circle cx="200" cy="200" r="175" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1" />
        <circle cx="200" cy="200" r="155" fill="none" stroke="rgba(147,197,253,0.5)" strokeWidth="0.8" strokeDasharray="6 4" />
        <circle cx="200" cy="200" r="135" fill="none" stroke="rgba(186,230,253,0.45)" strokeWidth="0.6" />
        <path d="M200 10 L200 55 M200 390 L200 345 M10 200 L55 200 M390 200 L345 200" stroke="rgba(186,230,253,0.75)" strokeWidth="3" />
        <path d="M40 40 L75 75 M360 40 L325 75 M40 360 L75 325 M360 360 L325 325" stroke="rgba(147,197,253,0.6)" strokeWidth="2" />
        <path d="M200 25 L185 200 L200 175 L215 200 Z" fill="rgba(255,255,255,0.7)" />
        <path d="M200 375 L185 200 L200 225 L215 200 Z" fill="rgba(147,197,253,0.55)" />
        <circle cx="200" cy="200" r="25" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="3" />
        <circle cx="200" cy="200" r="15" fill="none" stroke="rgba(186,230,253,0.55)" strokeWidth="2" />
        <circle cx="200" cy="200" r="6" fill="rgba(186,230,253,0.75)" />
        {[...Array(72)].map((_, i) => (
          <line
            key={i}
            x1="200"
            y1={i % 9 === 0 ? "8" : i % 3 === 0 ? "14" : "18"}
            x2="200"
            y2="32"
            stroke={i % 9 === 0 ? "rgba(255,255,255,0.8)" : i % 3 === 0 ? "rgba(186,230,253,0.65)" : "rgba(147,197,253,0.5)"}
            strokeWidth={i % 9 === 0 ? "2.5" : i % 3 === 0 ? "1.5" : "0.9"}
            transform={`rotate(${i * 5} 200 200)`}
          />
        ))}
      </svg>
      
      {/* Mobile version - different position and potentially different size */}
      <svg 
        className="compass-slow-rotate absolute pointer-events-none block md:hidden"
        style={{ ...mobilePos, width: actualMobileSize, height: actualMobileSize, opacity }}
        viewBox="0 0 400 400"
      >
        <circle cx="200" cy="200" r="195" fill="none" stroke="rgba(186,230,253,0.7)" strokeWidth="1.2" />
        <circle cx="200" cy="200" r="175" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1" />
        <circle cx="200" cy="200" r="155" fill="none" stroke="rgba(147,197,253,0.5)" strokeWidth="0.8" strokeDasharray="6 4" />
        <circle cx="200" cy="200" r="135" fill="none" stroke="rgba(186,230,253,0.45)" strokeWidth="0.6" />
        <path d="M200 10 L200 55 M200 390 L200 345 M10 200 L55 200 M390 200 L345 200" stroke="rgba(186,230,253,0.75)" strokeWidth="3" />
        <path d="M40 40 L75 75 M360 40 L325 75 M40 360 L75 325 M360 360 L325 325" stroke="rgba(147,197,253,0.6)" strokeWidth="2" />
        <path d="M200 25 L185 200 L200 175 L215 200 Z" fill="rgba(255,255,255,0.7)" />
        <path d="M200 375 L185 200 L200 225 L215 200 Z" fill="rgba(147,197,253,0.55)" />
        <circle cx="200" cy="200" r="25" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="3" />
        <circle cx="200" cy="200" r="15" fill="none" stroke="rgba(186,230,253,0.55)" strokeWidth="2" />
        <circle cx="200" cy="200" r="6" fill="rgba(186,230,253,0.75)" />
        {[...Array(72)].map((_, i) => (
          <line
            key={i}
            x1="200"
            y1={i % 9 === 0 ? "8" : i % 3 === 0 ? "14" : "18"}
            x2="200"
            y2="32"
            stroke={i % 9 === 0 ? "rgba(255,255,255,0.8)" : i % 3 === 0 ? "rgba(186,230,253,0.65)" : "rgba(147,197,253,0.5)"}
            strokeWidth={i % 9 === 0 ? "2.5" : i % 3 === 0 ? "1.5" : "0.9"}
            transform={`rotate(${i * 5} 200 200)`}
          />
        ))}
      </svg>
    </>
  );
};

const ModuleShowcase = ({ mod, index }) => {
  const isEven = index % 2 === 0;
  const Icon = mod.icon;
  
  // Creative alternating pattern for BOTH desktop and mobile: RIGHT, LEFT, RIGHT, LEFT, RIGHT, LEFT
  // Varying positions for visual interest (top, middle, bottom)
  const compassPositions = [
    { position: 'top-right', mobilePosition: 'mobile-top-right', size: 550, mobileSize: 550, opacity: 0.18 },           // 0: Strategy Dev - RIGHT (top)
    { position: 'middle-left', mobilePosition: 'mobile-middle-left', size: 500, mobileSize: 600, opacity: 0.18 },       // 1: Execution - LEFT (middle)
    { position: 'bottom-right', mobilePosition: 'mobile-top-right', size: 520, mobileSize: 550, opacity: 0.18 },        // 2: Portfolio - RIGHT (bottom)
    { position: 'top-left', mobilePosition: 'mobile-middle-left', size: 550, mobileSize: 600, opacity: 0.18 },          // 3: Project - LEFT (top)
    { position: 'middle-right', mobilePosition: 'mobile-middle-right', size: 500, mobileSize: 600, opacity: 0.18 },     // 4: Meeting - RIGHT (middle)
    { position: 'bottom-left', mobilePosition: 'mobile-middle-left', size: 520, mobileSize: 600, opacity: 0.18 },       // 5: Action Items - LEFT (bottom)
  ];
  
  const compassConfig = compassPositions[index] || compassPositions[0];

  return (
    <div className="py-24 md:py-32 px-6 relative overflow-hidden" data-testid={`module-showcase-${index}`}>
      {/* Background glow */}
      <div
        className={`absolute ${isEven ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-[600px] h-[500px] ${mod.glowColor} blur-[180px] opacity-[0.06] pointer-events-none`}
      />
      
      {/* Compass fragment - portion showing from edge */}
      <CompassFragment {...compassConfig} />

      <div className={`max-w-7xl mx-auto flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12 md:gap-20`}>
        {/* Text content */}
        <Reveal direction={isEven ? 'left' : 'right'} className="flex-1 max-w-xl">
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${mod.gradientFrom} ${mod.gradientTo} flex items-center justify-center mb-6 shadow-lg`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">{mod.title}</h3>
          <p className="text-lg text-gray-400 mb-8 leading-relaxed">{mod.description}</p>
          <ul className="space-y-3">
            {mod.features.map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${mod.gradientFrom} ${mod.gradientTo} flex items-center justify-center flex-shrink-0`}>
                  <Check className="w-3 h-3 text-white" />
                </div>
                {f}
              </li>
            ))}
          </ul>
        </Reveal>

        {/* Mockup */}
        <Reveal direction={isEven ? 'right' : 'left'} delay={0.15} className="flex-1 w-full max-w-lg">
          <mod.Mockup />
        </Reveal>
      </div>

      {/* Divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
    </div>
  );
};

/* ── Modules Burst from Logo ── */
const burstCards = [
  // Left side (animate left)
  { coloredWord: 'Strategy', rest: 'Development', desc: 'AI-guided strategic planning with document generation', titleGradient: 'from-[#06B6D4] via-[#0891B2] to-[#0E7490]', gradient: 'from-cyan-600/20 via-sky-600/10 to-transparent' },
  { coloredWord: 'Strategy', rest: 'Execution', desc: 'Balanced Scorecard performance management & KPI tracking', titleGradient: 'from-[#14B8A6] via-[#0D9488] to-[#0F766E]', gradient: 'from-teal-600/20 via-cyan-600/10 to-transparent' },
  { coloredWord: 'Portfolio', rest: 'Management', desc: 'Oversee project portfolios, track updates & escalations', titleGradient: 'from-[#EC4899] via-[#DB2777] to-[#BE185D]', gradient: 'from-fuchsia-600/20 via-pink-600/10 to-transparent' },
  // Right side (animate right)
  { coloredWord: 'Project', rest: 'Management', desc: 'Manage projects, tasks, timelines and team collaboration', titleGradient: 'from-[#8B5CF6] via-[#7C3AED] to-[#6D28D9]', gradient: 'from-violet-600/20 via-purple-600/10 to-transparent' },
  { coloredWord: 'Meeting', rest: 'Management', desc: 'Schedule, track and manage meetings effectively', titleGradient: 'from-[#F97316] via-[#EA580C] to-[#C2410C]', gradient: 'from-orange-600/20 via-amber-600/10 to-transparent' },
  { coloredWord: 'Action', rest: 'Items Management', desc: 'Track and manage action items across projects', titleGradient: 'from-[#F59E0B] via-[#D97706] to-[#B45309]', gradient: 'from-amber-600/20 via-orange-600/10 to-transparent' },
];

const ModulesBurst = ({ scrollToSection }) => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(entry.target); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const left = burstCards.slice(0, 3);
  const right = burstCards.slice(3);

  return (
    <section
      id="modules-burst"
      ref={sectionRef}
      className="relative py-16 md:py-20 flex flex-col items-center justify-center overflow-hidden"
      data-testid="modules-burst"
    >
      <style>{`
        @keyframes slideLeft  { from { opacity:0; transform:translateX(80px) scale(0.9); } to { opacity:1; transform:translateX(0) scale(1); } }
        @keyframes slideRight { from { opacity:0; transform:translateX(-80px) scale(0.9); } to { opacity:1; transform:translateX(0) scale(1); } }
        @keyframes logoFade   { from { opacity:0; transform:scale(0.6); } to { opacity:1; transform:scale(1); } }
        .burst-card { opacity:0; will-change:transform,opacity; }
        .burst-go .burst-logo-c { animation: logoFade 0.6s cubic-bezier(.22,1,.36,1) forwards; }
        .burst-go .slide-l { animation: slideLeft 0.6s cubic-bezier(.22,1,.36,1) forwards; }
        .burst-go .slide-r { animation: slideRight 0.6s cubic-bezier(.22,1,.36,1) forwards; }
      `}</style>

      <div className={`w-full max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-6 md:gap-10 ${visible ? 'burst-go' : ''}`}>
        {/* Left column — 3 cards */}
        <div className="w-full md:flex-1 flex flex-col gap-4">
          {left.map((card, i) => (
            <div
              key={card.coloredWord + card.rest}
              className={`burst-card slide-l relative group rounded-2xl p-5 overflow-hidden bg-[#0f1729]/60 backdrop-blur-sm border border-white/5 hover:border-white/10 transition-all duration-300`}
              style={{ animationDelay: `${0.2 + i * 0.12}s` }}
              data-testid={`burst-card-left-${i}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-50`} />
              <div className="relative z-10">
                <h4 className="text-lg md:text-xl font-bold mb-1.5">
                  <span className={`bg-gradient-to-r ${card.titleGradient} bg-clip-text text-transparent`}>{card.coloredWord}</span>
                  <span className="text-white/90 ml-1.5">{card.rest}</span>
                </h4>
                <p className="text-sm text-gray-400 leading-relaxed">{card.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Center logo */}
        <div className="burst-logo-c opacity-0 flex-shrink-0 order-first md:order-none">
          <div className="w-32 h-32 md:w-52 md:h-52 rounded-full overflow-hidden border-2 border-white/10 shadow-[0_0_60px_-10px_rgba(28,61,89,.5)] flex items-center justify-center bg-white">
            <img src="/logos/logo-circle-wolf.png" alt="Strategy Wolves" className="w-[90%] h-[90%] object-contain" />
          </div>
        </div>

        {/* Right column — 3 cards */}
        <div className="w-full md:flex-1 flex flex-col gap-4">
          {right.map((card, i) => (
            <div
              key={card.coloredWord + card.rest}
              className={`burst-card slide-r relative group rounded-2xl p-5 overflow-hidden bg-[#0f1729]/60 backdrop-blur-sm border border-white/5 hover:border-white/10 transition-all duration-300`}
              style={{ animationDelay: `${0.2 + i * 0.12}s` }}
              data-testid={`burst-card-right-${i}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-50`} />
              <div className="relative z-10">
                <h4 className="text-lg md:text-xl font-bold mb-1.5">
                  <span className={`bg-gradient-to-r ${card.titleGradient} bg-clip-text text-transparent`}>{card.coloredWord}</span>
                  <span className="text-white/90 ml-1.5">{card.rest}</span>
                </h4>
                <p className="text-sm text-gray-400 leading-relaxed">{card.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll down */}
      <div className="mt-16" style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.5s 1s' }}>
        <button
          onClick={() => scrollToSection('features')}
          className="text-white/30 hover:text-white transition-colors animate-bounce"
          data-testid="burst-scroll-down"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 13l5 5 5-5M7 6l5 5 5-5"/></svg>
        </button>
      </div>
    </section>
  );
};

/* ════════════════════ LANDING PAGE ════════════════════ */
const LandingPage = () => {
  const navigate = useNavigate();

  // Fix scrolling: override the global overflow:hidden from App.css
  useEffect(() => {
    document.documentElement.classList.add('dark');

    const html = document.documentElement;
    const body = document.body;
    const appEl = document.querySelector('.App');

    // Save originals
    const origHtmlOverflow = html.style.overflow;
    const origHtmlHeight = html.style.height;
    const origBodyOverflow = body.style.overflow;
    const origBodyHeight = body.style.height;
    const origAppHeight = appEl?.style.height;
    const origAppOverflow = appEl?.style.overflow;

    // Enable scrolling — override App.css { height:100%; overflow:hidden }
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    if (appEl) {
      appEl.style.height = 'auto';
      appEl.style.overflow = 'visible';
    }

    return () => {
      // Restore originals
      html.style.overflow = origHtmlOverflow;
      html.style.height = origHtmlHeight;
      body.style.overflow = origBodyOverflow;
      body.style.height = origBodyHeight;
      if (appEl) {
        appEl.style.height = origAppHeight;
        appEl.style.overflow = origAppOverflow;
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-blue-500/30 overflow-x-hidden" data-testid="landing-page">
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        @keyframes floatLogo { 0%,100%{transform:translate(-50%,-50%) scale(1)} 50%{transform:translate(-50%,-50%) scale(1.04)} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes rotateCompass { from { transform: translate(-50%, 0) rotate(0deg); } to { transform: translate(-50%, 0) rotate(360deg); } }
        @keyframes rotateCompassCenter { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulseGlow { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes floatParticle { 0%, 100% { transform: translateY(0) scale(1); opacity: 0.2; } 50% { transform: translateY(-30px) scale(1.3); opacity: 0.5; } }
        @keyframes subtlePulse { 0%, 100% { opacity: 0.03; } 50% { opacity: 0.06; } }
        .fade-up { animation: fadeUp .85s ease-out forwards; opacity:0; }
        .d1{animation-delay:.12s} .d2{animation-delay:.24s} .d3{animation-delay:.36s} .d4{animation-delay:.48s} .d5{animation-delay:.7s}
        .compass-slow-rotate { animation: rotateCompass 180s linear infinite; }
        .compass-slow-rotate-reverse { animation: rotateCompass 150s linear infinite reverse; }
        .compass-rotate-center { animation: rotateCompassCenter 200s linear infinite; }
        .glow-pulse { animation: pulseGlow 5s ease-in-out infinite; }
        .particle-float { animation: floatParticle 6s ease-in-out infinite; }
        .subtle-pulse { animation: subtlePulse 8s ease-in-out infinite; }
      `}</style>

      {/* ── Navbar ── */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-black/60" data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logos/logo-circle-wolf.png" alt="Strategy Wolves" className="w-8 h-8 rounded-full object-contain" />
            <span className="text-xl font-bold tracking-tight text-white">Strategy Wolves</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('features')} className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="nav-features-btn">Features</button>
            <button onClick={() => scrollToSection('pricing')} className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="nav-pricing-btn">Pricing</button>
            <Link to="/login" className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="nav-login-btn">Login</Link>
            <Button onClick={() => navigate('/signup')} className="bg-white text-black hover:bg-gray-200 rounded-full px-6 py-2 h-auto text-sm font-medium" data-testid="nav-signup-btn">Get Started</Button>
          </div>
        </div>
      </nav>

      {/* ── Hero with Integrated Celestial Compass ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden" data-testid="hero-section">
        
        {/* === CELESTIAL COMPASS BACKGROUND === */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          
          {/* === DESKTOP VERSION: Arc at bottom rising up === */}
          <div className="hidden md:contents">
            {/* Main celestial arc - huge, dramatic, rises into content area */}
            <div 
              className="absolute left-1/2 -translate-x-1/2 rounded-full glow-pulse hidden md:block"
              style={{
                width: '250vw',
                height: '250vw',
                maxWidth: '2800px',
                maxHeight: '2800px',
                bottom: '-110vw',
                background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.18) 0%, rgba(224,242,254,0.14) 10%, rgba(186,230,253,0.12) 20%, rgba(147,197,253,0.09) 30%, rgba(96,165,250,0.06) 40%, rgba(59,130,246,0.03) 50%, transparent 60%)',
                boxShadow: 'inset 0 300px 400px -150px rgba(255,255,255,0.25), 0 0 400px 150px rgba(186,230,253,0.12), 0 0 200px 80px rgba(147,197,253,0.15)'
              }}
            />
            
            {/* Secondary bright ring for edge definition */}
            <div 
              className="absolute left-1/2 -translate-x-1/2 rounded-full hidden md:block"
              style={{
                width: '240vw',
                height: '240vw',
                maxWidth: '2700px',
                maxHeight: '2700px',
                bottom: '-108vw',
                background: 'radial-gradient(ellipse at center, transparent 48%, rgba(255,255,255,0.12) 49%, rgba(224,242,254,0.08) 50%, rgba(186,230,253,0.05) 52%, transparent 55%)',
              }}
            />
            
            {/* Inner glow layer */}
            <div 
              className="absolute left-1/2 -translate-x-1/2 rounded-full hidden md:block"
              style={{
                width: '220vw',
                height: '220vw',
                maxWidth: '2500px',
                maxHeight: '2500px',
                bottom: '-105vw',
                background: 'radial-gradient(ellipse at center, transparent 45%, rgba(255,255,255,0.06) 50%, transparent 58%)',
              }}
            />
            
            {/* Compass SVG - Large, behind hero content, slowly rotating */}
            <svg 
              className="compass-slow-rotate absolute left-1/2 opacity-[0.07] hidden md:block" 
              style={{ bottom: '-45vw', width: '100vw', height: '100vw', maxWidth: '1200px', maxHeight: '1200px' }}
              viewBox="0 0 400 400"
            >
              {/* Outer decorative rings */}
              <circle cx="200" cy="200" r="198" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.4" />
              <circle cx="200" cy="200" r="190" fill="none" stroke="rgba(224,242,254,0.5)" strokeWidth="0.6" />
              <circle cx="200" cy="200" r="175" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.4" strokeDasharray="1 3" />
              <circle cx="200" cy="200" r="160" fill="none" stroke="rgba(186,230,253,0.4)" strokeWidth="0.5" />
              <circle cx="200" cy="200" r="145" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.4" strokeDasharray="6 3" />
              <circle cx="200" cy="200" r="130" fill="none" stroke="rgba(224,242,254,0.3)" strokeWidth="0.4" />
              
              {/* Cardinal direction lines - prominent */}
              <path d="M200 5 L200 55" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" />
              <path d="M200 395 L200 345" stroke="rgba(186,230,253,0.5)" strokeWidth="2" />
              <path d="M5 200 L55 200" stroke="rgba(186,230,253,0.5)" strokeWidth="2" />
              <path d="M395 200 L345 200" stroke="rgba(186,230,253,0.5)" strokeWidth="2" />
              
              {/* Intercardinal lines */}
              <path d="M35 35 L70 70" stroke="rgba(224,242,254,0.4)" strokeWidth="1.5" />
              <path d="M365 35 L330 70" stroke="rgba(224,242,254,0.4)" strokeWidth="1.5" />
              <path d="M35 365 L70 330" stroke="rgba(224,242,254,0.4)" strokeWidth="1.5" />
              <path d="M365 365 L330 330" stroke="rgba(224,242,254,0.4)" strokeWidth="1.5" />
              
              {/* Compass needle - North (pointing up) */}
              <path d="M200 15 L182 200 L200 168 L218 200 Z" fill="rgba(255,255,255,0.6)" />
              {/* Compass needle - South */}
              <path d="M200 385 L182 200 L200 232 L218 200 Z" fill="rgba(186,230,253,0.3)" />
              
              {/* Center hub */}
              <circle cx="200" cy="200" r="28" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2.5" />
              <circle cx="200" cy="200" r="18" fill="none" stroke="rgba(224,242,254,0.35)" strokeWidth="1.5" />
              <circle cx="200" cy="200" r="8" fill="rgba(255,255,255,0.5)" />
              
              {/* Degree tick marks all around */}
              {[...Array(72)].map((_, i) => (
                <line
                  key={i}
                  x1="200"
                  y1={i % 9 === 0 ? "8" : i % 3 === 0 ? "14" : "18"}
                  x2="200"
                  y2="25"
                  stroke={i % 9 === 0 ? "rgba(255,255,255,0.7)" : i % 3 === 0 ? "rgba(224,242,254,0.5)" : "rgba(186,230,253,0.3)"}
                  strokeWidth={i % 9 === 0 ? "2" : i % 3 === 0 ? "1" : "0.5"}
                  transform={`rotate(${i * 5} 200 200)`}
                />
              ))}
            </svg>
            
            {/* Outer rotating ring - opposite direction */}
            <svg 
              className="compass-slow-rotate-reverse absolute left-1/2 opacity-[0.04] hidden md:block" 
              style={{ bottom: '-55vw', width: '130vw', height: '130vw', maxWidth: '1500px', maxHeight: '1500px' }}
              viewBox="0 0 400 400"
            >
              <circle cx="200" cy="200" r="198" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.6" strokeDasharray="2 8" />
              <circle cx="200" cy="200" r="195" fill="none" stroke="rgba(224,242,254,0.5)" strokeWidth="0.4" strokeDasharray="4 12" />
            </svg>
            
            {/* Floating particles - desktop */}
            <div className="particle-float absolute w-1.5 h-1.5 bg-white/50 rounded-full hidden md:block" style={{ top: '15%', left: '12%', animationDelay: '0s' }} />
            <div className="particle-float absolute w-2 h-2 bg-blue-200/40 rounded-full hidden md:block" style={{ top: '25%', left: '85%', animationDelay: '1s' }} />
            <div className="particle-float absolute w-1 h-1 bg-white/40 rounded-full hidden md:block" style={{ top: '45%', left: '8%', animationDelay: '2s' }} />
            <div className="particle-float absolute w-1 h-1 bg-blue-300/60 rounded-full hidden md:block" style={{ top: '12%', left: '65%', animationDelay: '3s' }} />
            <div className="particle-float absolute w-1.5 h-1.5 bg-white/35 rounded-full hidden md:block" style={{ top: '35%', left: '90%', animationDelay: '1.5s' }} />
            <div className="particle-float absolute w-0.5 h-0.5 bg-blue-200/50 rounded-full hidden md:block" style={{ top: '55%', left: '20%', animationDelay: '2.5s' }} />
            <div className="particle-float absolute w-2 h-2 bg-white/25 rounded-full hidden md:block" style={{ top: '20%', left: '35%', animationDelay: '4s' }} />
            <div className="particle-float absolute w-1 h-1 bg-blue-100/45 rounded-full hidden md:block" style={{ top: '40%', left: '75%', animationDelay: '0.5s' }} />
          </div>
          
          {/* === MOBILE VERSION: Arc at BOTTOM curving up through Strategy Wolves. === */}
          {/* Bottom arc - positioned so curve passes through title, edges align with S and . */}
          <div 
            className="absolute left-1/2 -translate-x-1/2 rounded-full block md:hidden"
            style={{
              width: '500vw',
              height: '500vw',
              bottom: '-262vw',
              background: 'radial-gradient(ellipse at center, rgba(10,14,20,0.98) 0%, rgba(8,12,18,0.95) 46%, rgba(5,8,14,0.3) 49%, transparent 50.5%)',
            }}
          />
          
          {/* Arc edge glow - subtle blue accent */}
          <div 
            className="absolute left-1/2 -translate-x-1/2 rounded-full block md:hidden"
            style={{
              width: '500vw',
              height: '500vw',
              bottom: '-262vw',
              background: 'radial-gradient(ellipse at center, transparent 48.5%, rgba(186,230,253,0.12) 49.3%, rgba(147,197,253,0.06) 50%, transparent 51%)',
            }}
          />
          
          {/* Compass at bottom - mobile - brighter details */}
          <svg 
            className="compass-slow-rotate absolute left-1/2 opacity-[0.25] block md:hidden" 
            style={{ bottom: '-30%', width: '140vw', height: '140vw' }}
            viewBox="0 0 400 400"
          >
            <circle cx="200" cy="200" r="190" fill="none" stroke="rgba(150,190,220,0.5)" strokeWidth="0.8" />
            <circle cx="200" cy="200" r="170" fill="none" stroke="rgba(120,160,190,0.4)" strokeWidth="0.6" />
            <circle cx="200" cy="200" r="150" fill="none" stroke="rgba(150,190,220,0.35)" strokeWidth="0.5" strokeDasharray="4 4" />
            
            {/* Cardinal lines */}
            <path d="M200 15 L200 50" stroke="rgba(180,210,230,0.7)" strokeWidth="2" />
            <path d="M200 385 L200 350" stroke="rgba(140,180,210,0.5)" strokeWidth="1.5" />
            <path d="M15 200 L50 200" stroke="rgba(140,180,210,0.5)" strokeWidth="1.5" />
            <path d="M385 200 L350 200" stroke="rgba(140,180,210,0.5)" strokeWidth="1.5" />
            
            {/* Intercardinal lines */}
            <path d="M45 45 L75 75" stroke="rgba(140,180,210,0.4)" strokeWidth="1.2" />
            <path d="M355 45 L325 75" stroke="rgba(140,180,210,0.4)" strokeWidth="1.2" />
            <path d="M45 355 L75 325" stroke="rgba(140,180,210,0.4)" strokeWidth="1.2" />
            <path d="M355 355 L325 325" stroke="rgba(140,180,210,0.4)" strokeWidth="1.2" />
            
            {/* Compass needle */}
            <path d="M200 25 L188 200 L200 180 L212 200 Z" fill="rgba(180,210,230,0.5)" />
            <path d="M200 375 L188 200 L200 220 L212 200 Z" fill="rgba(140,180,210,0.3)" />
            
            {/* Center */}
            <circle cx="200" cy="200" r="20" fill="none" stroke="rgba(180,210,230,0.5)" strokeWidth="2" />
            <circle cx="200" cy="200" r="6" fill="rgba(180,210,230,0.6)" />
            
            {/* Tick marks - brighter |i|i|i pattern */}
            {[...Array(72)].map((_, i) => (
              <line
                key={i}
                x1="200"
                y1={i % 9 === 0 ? "8" : i % 3 === 0 ? "14" : "18"}
                x2="200"
                y2="28"
                stroke={i % 9 === 0 ? "rgba(200,220,240,0.7)" : i % 3 === 0 ? "rgba(180,210,230,0.5)" : "rgba(150,190,220,0.35)"}
                strokeWidth={i % 9 === 0 ? "1.5" : i % 3 === 0 ? "1" : "0.6"}
                transform={`rotate(${i * 5} 200 200)`}
              />
            ))}
          </svg>
          
          {/* Floating particles - mobile - subtle */}
          <div className="particle-float absolute w-1 h-1 bg-white/20 rounded-full block md:hidden" style={{ top: '8%', left: '15%', animationDelay: '0s' }} />
          <div className="particle-float absolute w-0.5 h-0.5 bg-blue-300/15 rounded-full block md:hidden" style={{ top: '15%', left: '80%', animationDelay: '2s' }} />
          <div className="particle-float absolute w-1 h-1 bg-white/15 rounded-full block md:hidden" style={{ top: '5%', left: '50%', animationDelay: '1s' }} />
          <div className="particle-float absolute w-0.5 h-0.5 bg-blue-200/15 rounded-full block md:hidden" style={{ top: '25%', left: '10%', animationDelay: '3s' }} />
          <div className="particle-float absolute w-0.5 h-0.5 bg-white/20 rounded-full block md:hidden" style={{ top: '20%', left: '90%', animationDelay: '1.5s' }} />
        </div>

        {/* === HERO CONTENT - Above the compass === */}
        <div className="relative z-10 pt-32 pb-40 md:pt-40 md:pb-48 px-6 flex flex-col items-center text-center max-w-7xl mx-auto">
          <h1 className="fade-up d1 text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-white mb-4 max-w-4xl" data-testid="hero-title">
            <span className="text-white">Strategy</span>{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#c0ddef] via-[#a8cfe5] to-[#90c0db]">Wolves.</span>
          </h1>

          <p className="fade-up d2 text-xl md:text-2xl lg:text-3xl font-semibold mb-10 text-white/80 tracking-wide" data-testid="hero-tagline">
            <span style={{ textShadow: '0 0 14px rgba(255,255,255,0.35), 0 0 40px rgba(255,255,255,0.12)' }}>Strategy. Executed.</span>
          </p>

          <p className="fade-up d3 text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed">
            The all-in-one platform for corporate strategy management. Bridge the gap between vision and victory.
          </p>

          <div className="fade-up d4">
            <Button
              variant="outline"
              onClick={() => scrollToSection('modules-heading')}
              className="bg-transparent border-white/20 text-white hover:bg-white/5 rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm"
              data-testid="hero-secondary-btn"
            >
              See How It Works <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── "Everything you need" heading ── */}
      <div id="modules-heading" className="pt-28 pb-16 md:pt-28 md:pb-20 px-6 text-center relative bg-black">
        {/* Clean section - no compass */}
        <Reveal direction="up">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-white relative z-10">Everything you need.</h2>
        </Reveal>
        <Reveal direction="up" delay={0.12}>
          <p className="text-lg md:text-xl text-gray-500 max-w-xl mx-auto relative z-10">Six powerful modules. One unified platform. Zero friction.</p>
        </Reveal>
      </div>

      {/* ── Modules Burst from Logo ── */}
      <ModulesBurst scrollToSection={scrollToSection} />

      {/* ── Features: Module Showcases ── */}
      <section id="features" data-testid="features-section">
        {/* Each module as a full-width alternating showcase */}
        {modules.map((mod, i) => (
          <ModuleShowcase key={mod.title} mod={mod} index={i} />
        ))}
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-32 px-6 bg-gradient-to-b from-black to-[#050505] relative overflow-hidden" data-testid="pricing-section">
        {/* Creative compass elements for pricing */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Large compass emerging from left side - DESKTOP */}
          <svg 
            className="compass-slow-rotate absolute opacity-[0.12] hidden md:block" 
            style={{ top: '10%', left: '-12%', width: '550px', height: '550px' }}
            viewBox="0 0 400 400"
          >
            <circle cx="200" cy="200" r="195" fill="none" stroke="rgba(186,230,253,0.5)" strokeWidth="0.8" />
            <circle cx="200" cy="200" r="175" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
            <circle cx="200" cy="200" r="155" fill="none" stroke="rgba(147,197,253,0.35)" strokeWidth="0.5" strokeDasharray="6 4" />
            <path d="M200 10 L200 55 M200 390 L200 345 M10 200 L55 200 M390 200 L345 200" stroke="rgba(186,230,253,0.6)" strokeWidth="2" />
            <path d="M200 25 L185 200 L200 175 L215 200 Z" fill="rgba(255,255,255,0.5)" />
            <path d="M200 375 L185 200 L200 225 L215 200 Z" fill="rgba(147,197,253,0.35)" />
            <circle cx="200" cy="200" r="22" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2.5" />
            <circle cx="200" cy="200" r="7" fill="rgba(186,230,253,0.55)" />
            {[...Array(72)].map((_, i) => (
              <line
                key={i}
                x1="200"
                y1={i % 9 === 0 ? "8" : i % 3 === 0 ? "14" : "18"}
                x2="200"
                y2="30"
                stroke={i % 9 === 0 ? "rgba(255,255,255,0.6)" : i % 3 === 0 ? "rgba(186,230,253,0.5)" : "rgba(147,197,253,0.35)"}
                strokeWidth={i % 9 === 0 ? "1.5" : i % 3 === 0 ? "1" : "0.6"}
                transform={`rotate(${i * 5} 200 200)`}
              />
            ))}
          </svg>
          
          {/* Smaller compass emerging from bottom right - DESKTOP */}
          <svg 
            className="compass-slow-rotate-reverse absolute opacity-[0.1] hidden md:block" 
            style={{ bottom: '-5%', right: '-8%', width: '450px', height: '450px' }}
            viewBox="0 0 400 400"
          >
            <circle cx="200" cy="200" r="190" fill="none" stroke="rgba(147,197,253,0.6)" strokeWidth="0.7" />
            <circle cx="200" cy="200" r="165" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" strokeDasharray="4 4" />
            <path d="M200 20 L200 55 M200 380 L200 345 M20 200 L55 200 M380 200 L345 200" stroke="rgba(147,197,253,0.55)" strokeWidth="1.8" />
            <path d="M200 40 L190 200 L200 180 L210 200 Z" fill="rgba(147,197,253,0.45)" />
            <circle cx="200" cy="200" r="15" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
            <circle cx="200" cy="200" r="5" fill="rgba(147,197,253,0.55)" />
            {[...Array(36)].map((_, i) => (
              <line
                key={i}
                x1="200"
                y1={i % 9 === 0 ? "12" : "18"}
                x2="200"
                y2="28"
                stroke={i % 9 === 0 ? "rgba(255,255,255,0.5)" : "rgba(147,197,253,0.35)"}
                strokeWidth={i % 9 === 0 ? "1.2" : "0.6"}
                transform={`rotate(${i * 10} 200 200)`}
              />
            ))}
          </svg>
          
          {/* MOBILE - compass from right side - MORE VISIBLE */}
          <svg 
            className="compass-slow-rotate absolute opacity-[0.18] block md:hidden" 
            style={{ top: '15%', right: '-20%', width: '320px', height: '320px' }}
            viewBox="0 0 400 400"
          >
            <circle cx="200" cy="200" r="190" fill="none" stroke="rgba(186,230,253,0.6)" strokeWidth="1" />
            <circle cx="200" cy="200" r="165" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.7" strokeDasharray="4 4" />
            <path d="M200 20 L200 55 M200 380 L200 345 M20 200 L55 200 M380 200 L345 200" stroke="rgba(186,230,253,0.65)" strokeWidth="2" />
            <path d="M200 40 L190 200 L200 180 L210 200 Z" fill="rgba(255,255,255,0.55)" />
            <circle cx="200" cy="200" r="15" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
            <circle cx="200" cy="200" r="5" fill="rgba(186,230,253,0.65)" />
            {[...Array(36)].map((_, i) => (
              <line
                key={i}
                x1="200"
                y1={i % 9 === 0 ? "12" : "18"}
                x2="200"
                y2="28"
                stroke={i % 9 === 0 ? "rgba(255,255,255,0.6)" : "rgba(147,197,253,0.4)"}
                strokeWidth={i % 9 === 0 ? "1.5" : "0.8"}
                transform={`rotate(${i * 10} 200 200)`}
              />
            ))}
          </svg>
          
          {/* Subtle arc at bottom */}
          <div 
            className="absolute left-1/2 -translate-x-1/2 rounded-full opacity-30"
            style={{
              width: '200vw',
              height: '200vw',
              maxWidth: '2200px',
              maxHeight: '2200px',
              bottom: '-185vw',
              background: 'radial-gradient(ellipse at center, rgba(59,130,246,0.08) 0%, rgba(30,64,175,0.04) 30%, transparent 50%)',
            }}
          />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal direction="up" className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white">Choose your power.</h2>
            <p className="text-xl text-gray-400">Simple, transparent pricing for teams of all sizes.</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter */}
            <Reveal direction="up" delay={0}>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-8 flex flex-col h-full hover:border-white/20 transition-colors" data-testid="pricing-starter">
                <h3 className="text-xl font-medium text-gray-400 mb-2">Starter</h3>
                <div className="text-4xl font-bold mb-6 text-white">$0<span className="text-lg text-gray-500 font-normal">/mo</span></div>
                <p className="text-gray-400 mb-8 text-sm">Perfect for small teams getting started with strategy.</p>
                <ul className="space-y-4 mb-8 flex-1">
                  {['Up to 5 Users', 'Basic Strategy Docs', 'Action Items', '1 Project'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm text-gray-300"><Check size={16} className="text-blue-500" />{f}</li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full rounded-full border-white/20 hover:bg-white/10 text-white">Get Started</Button>
              </div>
            </Reveal>

            {/* Wolf Pack */}
            <Reveal direction="up" delay={0.12}>
              <div className="bg-[#0A0A0A] border border-blue-500/50 rounded-3xl p-8 flex flex-col relative shadow-[0_0_50px_-20px_rgba(59,130,246,.3)] md:scale-105 z-10 h-full" data-testid="pricing-wolf-pack">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Most Popular</div>
                <h3 className="text-xl font-medium text-white mb-2">Wolf Pack</h3>
                <div className="text-4xl font-bold mb-6 text-blue-400">$49<span className="text-lg text-gray-500 font-normal text-white">/mo</span></div>
                <p className="text-gray-400 mb-8 text-sm">For growing organizations that need full visibility.</p>
                <ul className="space-y-4 mb-8 flex-1">
                  {['Up to 20 Users', 'Unlimited Projects', 'Strategy Execution & KPIs', 'Meeting Management', 'Priority Support'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm font-medium text-gray-200"><Check size={16} className="text-blue-400" />{f}</li>
                  ))}
                </ul>
                <Button className="w-full rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20">Try Free</Button>
              </div>
            </Reveal>

            {/* Alpha */}
            <Reveal direction="up" delay={0.24}>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-8 flex flex-col h-full hover:border-white/20 transition-colors" data-testid="pricing-alpha">
                <h3 className="text-xl font-medium text-gray-400 mb-2">Alpha</h3>
                <div className="text-4xl font-bold mb-6 text-white">Custom</div>
                <p className="text-gray-400 mb-8 text-sm">For large enterprises requiring advanced control.</p>
                <ul className="space-y-4 mb-8 flex-1">
                  {['Unlimited Users', 'Dedicated Success Manager', 'SSO & Advanced Security', 'Custom Integrations', 'On-premise Deployment'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm text-gray-300"><Check size={16} className="text-blue-500" />{f}</li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full rounded-full border-white/20 hover:bg-white/10 text-white">Contact Sales</Button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-20 border-t border-white/10 bg-black relative overflow-hidden" data-testid="landing-footer">
        {/* Creative compass elements in footer */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Compass emerging from right side - DESKTOP */}
          <svg 
            className="compass-rotate-center absolute opacity-[0.1] hidden md:block" 
            style={{ top: '50%', right: '-10%', transform: 'translateY(-50%)', width: '450px', height: '450px' }}
            viewBox="0 0 400 400"
          >
            <circle cx="200" cy="200" r="190" fill="none" stroke="rgba(186,230,253,0.55)" strokeWidth="0.8" />
            <circle cx="200" cy="200" r="165" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" strokeDasharray="6 3" />
            <circle cx="200" cy="200" r="140" fill="none" stroke="rgba(147,197,253,0.35)" strokeWidth="0.5" />
            <path d="M200 15 L200 50 M200 385 L200 350 M15 200 L50 200 M385 200 L350 200" stroke="rgba(186,230,253,0.6)" strokeWidth="2" />
            <path d="M200 35 L185 200 L200 175 L215 200 Z" fill="rgba(255,255,255,0.5)" />
            <path d="M200 365 L185 200 L200 225 L215 200 Z" fill="rgba(186,230,253,0.35)" />
            <circle cx="200" cy="200" r="20" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="2" />
            <circle cx="200" cy="200" r="6" fill="rgba(186,230,253,0.6)" />
            {[...Array(36)].map((_, i) => (
              <line
                key={i}
                x1="200"
                y1={i % 9 === 0 ? "12" : "18"}
                x2="200"
                y2="28"
                stroke={i % 9 === 0 ? "rgba(255,255,255,0.55)" : "rgba(147,197,253,0.35)"}
                strokeWidth={i % 9 === 0 ? "1.2" : "0.6"}
                transform={`rotate(${i * 10} 200 200)`}
              />
            ))}
          </svg>
          
          {/* MOBILE - compass from left side - MORE VISIBLE */}
          <svg 
            className="compass-rotate-center absolute opacity-[0.18] block md:hidden" 
            style={{ top: '50%', left: '-25%', transform: 'translateY(-50%)', width: '280px', height: '280px' }}
            viewBox="0 0 400 400"
          >
            <circle cx="200" cy="200" r="190" fill="none" stroke="rgba(186,230,253,0.6)" strokeWidth="1" />
            <circle cx="200" cy="200" r="165" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.7" strokeDasharray="6 3" />
            <path d="M200 15 L200 50 M200 385 L200 350 M15 200 L50 200 M385 200 L350 200" stroke="rgba(186,230,253,0.65)" strokeWidth="2" />
            <path d="M200 35 L185 200 L200 175 L215 200 Z" fill="rgba(255,255,255,0.55)" />
            <circle cx="200" cy="200" r="20" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
            <circle cx="200" cy="200" r="6" fill="rgba(186,230,253,0.65)" />
            {[...Array(36)].map((_, i) => (
              <line
                key={i}
                x1="200"
                y1={i % 9 === 0 ? "12" : "18"}
                x2="200"
                y2="28"
                stroke={i % 9 === 0 ? "rgba(255,255,255,0.6)" : "rgba(147,197,253,0.4)"}
                strokeWidth={i % 9 === 0 ? "1.5" : "0.8"}
                transform={`rotate(${i * 10} 200 200)`}
              />
            ))}
          </svg>
          
          {/* Soft glow on the right */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl hidden md:block" />
          
          {/* Subtle arc line at top of footer */}
          <div 
            className="absolute left-1/2 -translate-x-1/2 rounded-full opacity-20"
            style={{
              width: '150vw',
              height: '150vw',
              top: '-145vw',
              background: 'radial-gradient(ellipse at center, transparent 48%, rgba(147,197,253,0.08) 49.5%, transparent 51%)',
            }}
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-12 relative z-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <img src="/logos/logo-circle-wolf.png" alt="Strategy Wolves" className="w-6 h-6 rounded object-cover" />
              <span className="text-lg font-bold text-white">Strategy Wolves</span>
            </div>
            <p className="text-gray-500 max-w-xs text-sm">Empowering leaders to execute strategy with precision and speed.</p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Product</h4>
            <ul className="space-y-4 text-sm text-gray-500">
              <li className="hover:text-white transition-colors cursor-pointer">Strategy</li>
              <li className="hover:text-white transition-colors cursor-pointer">Execution</li>
              <li className="hover:text-white transition-colors cursor-pointer">Portfolio</li>
              <li className="hover:text-white transition-colors cursor-pointer">Pricing</li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Company</h4>
            <ul className="space-y-4 text-sm text-gray-500">
              <li className="hover:text-white transition-colors cursor-pointer">About</li>
              <li className="hover:text-white transition-colors cursor-pointer">Blog</li>
              <li className="hover:text-white transition-colors cursor-pointer">Careers</li>
              <li className="hover:text-white transition-colors cursor-pointer">Contact</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-20 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-xs text-gray-600">
          <p>&copy; 2026 Strategy Wolves Inc. All rights reserved.</p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <span className="hover:text-gray-400 cursor-pointer">Privacy Policy</span>
            <span className="hover:text-gray-400 cursor-pointer">Terms of Service</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
